import Button from "@/components/Common/Button";
import Modal from "@/components/Common/Modal";
import InputField from "@/components/Common/forms/InputField";
import {useSendPackageMutation} from "@/hooks/useSendPackageMutation";
import {sendProductModalDataAtom} from "@/store/jotaiAtoms";
import {useAtom} from "jotai";
import {useState, type FC} from "react";
import {Trans, useTranslation} from "react-i18next";

const URL_REGEX = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,63})([/\w .,?&%=#-]*)*\/?$/i;

const SendProductModal: FC = () => {
  const {t} = useTranslation("campaign_preparation");
  const [sendProductModalData, setSendProductModalData] = useAtom(sendProductModalDataAtom);
  const applicationId = sendProductModalData?.applicationId!;
  const [trackingUrl, setTrackingUrl] = useState(sendProductModalData?.trackingUrl ?? "");
  const [trackingCode, setTrackingCode] = useState("");
  const [hasTouchedTrackingUrl, setHasTouchedTrackingUrl] = useState(false);
  const isTrackingUrlValid = !trackingUrl || URL_REGEX.test(trackingUrl);

  const {mutate: sendPackage, isPending} = useSendPackageMutation(applicationId, {
    onSuccess: () => setSendProductModalData(null),
  });

  return (
    <Modal
      onClose={() => setSendProductModalData(null)}
      modalTitle={t("send_product")}
      backgroundClassName="max-w-125"
      customBottomRow={
        <div className="border-neutral-white-dark flex shrink-0 grow p-5">
          <Button
            isDisabled={isPending || !isTrackingUrlValid}
            isLoading={isPending}
            // Blank inputs are omitted (undefined), not sent as null: the server may
            // have auto-extracted tracking in the meantime, and an explicit null
            // would wipe it. Submitting blank means "nothing to add", not "clear".
            onClick={() =>
              sendPackage({trackingCode: trackingCode || undefined, trackingUrl: trackingUrl || undefined})
            }
            buttonStyle="Gradient"
            className="w-full">
            <span className="z-10 font-medium md:text-lg">{t("send_product")}</span>
          </Button>
        </div>
      }>
      <div className="flex flex-col gap-4">
        <div className="bg-neutral-white-dark text-neutral-black rounded-md p-3 text-sm">
          <Trans t={t} i18nKey="send_product_modal_description" components={{1: <span className="font-semibold" />}} />
        </div>
        <InputField
          label={t("tracking_url")}
          optional
          value={trackingUrl}
          onChange={value => {
            setTrackingUrl(value);
            if (hasTouchedTrackingUrl) setHasTouchedTrackingUrl(false);
          }}
          onBlur={() => setHasTouchedTrackingUrl(true)}
          error={hasTouchedTrackingUrl && !isTrackingUrlValid ? "invalidUrl" : null}
          placeholder={t("tracking_url_placeholder")}
        />
        <InputField
          label={t("tracking_code")}
          optional
          value={trackingCode}
          onChange={setTrackingCode}
          placeholder={t("tracking_code_placeholder")}
        />
      </div>
    </Modal>
  );
};

export default SendProductModal;
